import { Component, inject, signal, output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Input } from '@arm-web/ui-components';
import { EncryptionService } from '../../../services/encryption.service';
import {
  ActionButtonsComponent,
  asActionButtonVariant,
} from '../../action-buttons/action-buttons';

@Component({
  selector: 'app-unlock-form',
  standalone: true,
  imports: [ReactiveFormsModule, Input, ActionButtonsComponent],
  templateUrl: './unlock-form.html',
  styleUrl: './unlock-form.css',
})
export class UnlockFormComponent {
  private readonly encryptionService = inject(EncryptionService);

  // Reactive form
  unlockForm = new FormGroup({
    encryptionKey: new FormControl('', [Validators.required]),
  });

  // Component signals
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string>('');

  // Output events
  dataUnlocked = output<void>();
  cancelled = output<void>();

  /**
   * Handles form submission to unlock data
   */
  handleUnlock(): void {
    // Reset error message
    this.errorMessage.set('');

    // Validate form
    if (this.unlockForm.invalid) {
      this.errorMessage.set('Please enter an encryption key');
      this.unlockForm.markAllAsTouched();
      return;
    }

    // Get form value
    const key = this.unlockForm.get('encryptionKey')?.value?.trim();
    if (!key) {
      this.errorMessage.set('Please enter an encryption key');
      return;
    }

    // Set loading state
    this.isSubmitting.set(true);

    // Call encryption service
    this.encryptionService.unlockData(key).subscribe({
      next: () => {
        location.reload();
      },
      error: error => {
        this.isSubmitting.set(false);
        this.errorMessage.set(
          error.message || 'Invalid encryption key. Please try again.'
        );
      },
    });
  }

  /**
   * Handles close button click
   */
  handleClose(): void {
    this.cancelled.emit();
  }

  asActionButtonVariant = asActionButtonVariant;

  /**
   * Handle action button clicks
   */
  onActionButtonClick(buttonId: string): void {
    switch (buttonId) {
      case 'unlock-data-btn':
        this.handleUnlock();
        break;
      case 'close-btn':
        this.handleClose();
        break;
    }
  }
}
